import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ArrowLeft, Send, User, Calendar, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SupportTicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newResponse, setNewResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchTicketDetails();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    try {
      const [ticketRes, responsesRes] = await Promise.all([
        axios.get(`${API}/support-tickets/${ticketId}`),
        axios.get(`${API}/support-tickets/${ticketId}/responses`)
      ]);
      
      setTicket(ticketRes.data);
      setResponses(responsesRes.data || []);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleSendResponse = async () => {
    if (!newResponse.trim()) {
      toast.error('Please enter a response');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/support-tickets/${ticketId}/responses`, {
        message: newResponse,
        is_admin: true
      });

      toast.success('Response sent');
      setNewResponse('');
      fetchTicketDetails();
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Failed to send response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/support-tickets/${ticketId}`, {
        status: newStatus
      });

      toast.success(`Ticket marked as ${newStatus}`);
      fetchTicketDetails();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-500',
      medium: 'bg-blue-500',
      high: 'bg-orange-500',
      urgent: 'bg-red-500'
    };
    return colors[priority] || 'bg-gray-500';
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Open' },
      in_progress: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', label: 'In Progress' },
      resolved: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Resolved' }
    };
    const variant = variants[status] || variants.open;
    return (
      <Badge className={`${variant.bg} ${variant.text} border-0`}>
        {variant.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading ticket...</div>
        </div>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <AlertCircle className="h-12 w-12 text-muted-foreground" />
          <div className="text-muted-foreground">Ticket not found</div>
          <Button onClick={() => navigate('/support-hub')}>Back to Support Hub</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/support-hub')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{ticket.title}</h1>
            <p className="text-sm text-muted-foreground">Ticket #{ticket.id.slice(0, 8)}</p>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(ticket.status)}
            <Badge className={getPriorityColor(ticket.priority)}>
              {ticket.priority}
            </Badge>
          </div>
        </div>

        {/* Ticket Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Original Ticket */}
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {ticket.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-semibold">{ticket.user_name}</div>
                      <div className="text-xs text-muted-foreground">{ticket.user_email}</div>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    <div>{new Date(ticket.submitted_at).toLocaleDateString()}</div>
                    <div>{new Date(ticket.submitted_at).toLocaleTimeString()}</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="prose dark:prose-invert max-w-none">
                  <p className="whitespace-pre-wrap">{ticket.description}</p>
                </div>

                {/* Screenshots */}
                {ticket.screenshots && ticket.screenshots.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium">Attachments:</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {ticket.screenshots.map((screenshot, index) => (
                        <a
                          key={index}
                          href={`${BACKEND_URL}${screenshot}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="border rounded-lg overflow-hidden hover:ring-2 ring-primary transition-all"
                        >
                          <img
                            src={`${BACKEND_URL}${screenshot}`}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-32 object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Responses/Conversation */}
            <Card>
              <CardHeader>
                <CardTitle>Conversation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {responses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No responses yet. Be the first to respond!
                  </div>
                ) : (
                  responses.map((response, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-lg ${
                        response.is_admin
                          ? 'bg-blue-50 dark:bg-blue-900/20 ml-8'
                          : 'bg-muted mr-8'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className={response.is_admin ? 'bg-blue-500' : 'bg-gray-500'}>
                            {response.responder_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{response.responder_name}</span>
                              {response.is_admin && (
                                <Badge variant="outline" className="text-xs">Admin</Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(response.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{response.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Response Input */}
                <div className="space-y-3 pt-4 border-t">
                  <Textarea
                    placeholder="Type your response to the user..."
                    value={newResponse}
                    onChange={(e) => setNewResponse(e.target.value)}
                    rows={4}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleSendResponse} disabled={submitting}>
                      <Send className="h-4 w-4 mr-2" />
                      {submitting ? 'Sending...' : 'Send Response'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Status Management */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select
                  value={ticket.status}
                  onValueChange={handleUpdateStatus}
                  disabled={updatingStatus}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>

                {ticket.status === 'resolved' && ticket.resolved_at && (
                  <div className="text-xs text-muted-foreground">
                    Resolved on {new Date(ticket.resolved_at).toLocaleDateString()}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ticket Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Ticket Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{ticket.user_name}</div>
                    <div className="text-xs text-muted-foreground">{ticket.user_email}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-muted-foreground text-xs">Submitted</div>
                    <div>{new Date(ticket.submitted_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {ticket.due_date && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-muted-foreground text-xs">Due Date</div>
                      <div>{new Date(ticket.due_date).toLocaleDateString()}</div>
                    </div>
                  </div>
                )}

                <div className="pt-2 border-t">
                  <div className="text-muted-foreground text-xs mb-1">Priority</div>
                  <Badge className={getPriorityColor(ticket.priority)}>
                    {ticket.priority.toUpperCase()}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {ticket.status !== 'resolved' && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => handleUpdateStatus('resolved')}
                    disabled={updatingStatus}
                  >
                    Mark as Resolved
                  </Button>
                )}
                {ticket.status === 'open' && (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => handleUpdateStatus('in_progress')}
                    disabled={updatingStatus}
                  >
                    Start Working
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};
